import { useState } from "react";

import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import { Project } from "../types";

interface ProjectCardProps {
  project: Project;
  onViewDetails: (project: Project) => void;
}

const ProjectCard = ({ project, onViewDetails }: ProjectCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case "Beginner":
        return "text-success bg-success/10";
      case "Intermediate":
        return "text-warning bg-warning/10";
      case "Advanced":
        return "text-error bg-error/10";
      default:
        return "text-muted-foreground bg-muted";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Completed":
        return "text-success bg-success/10";
      case "In Progress":
        return "text-warning bg-warning/10";
      case "Maintained":
        return "text-secondary bg-secondary/10";
      default:
        return "text-muted-foreground bg-muted";
    }
  };

  return (
    <div
      className="group relative bg-card rounded-lg border border-border overflow-hidden transition-all duration-300 hover:shadow-elevation-3 hover:border-primary/50"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative h-48 overflow-hidden bg-muted">
        <Image
          src={project.image}
          alt={project.alt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {project.featured && (
          <div className="absolute top-4 left-4 px-3 py-1 bg-accent text-accent-foreground rounded-full text-xs font-semibold flex items-center gap-1">
            <Icon name="Star" size={12} />
            Featured
          </div>
        )}

        <div className="absolute top-4 right-4 flex gap-2">
          {project.githubUrl && (
            <a
              href={project.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-background/80 backdrop-blur-sm rounded-full hover:bg-background transition-colors duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <Icon name="Github" size={16} color="var(--color-foreground)" />
            </a>
          )}
          {project.liveUrl && (
            <a
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-background/80 backdrop-blur-sm rounded-full hover:bg-background transition-colors duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <Icon name="ExternalLink" size={16} color="var(--color-foreground)" />
            </a>
          )}
        </div>

        {isHovered && (
          <div className="absolute bottom-4 left-4 right-4 flex gap-2 animate-fade-in">
            <Button
              variant="default"
              size="sm"
              iconName="Eye"
              iconPosition="left"
              onClick={() => onViewDetails(project)}
              className="flex-1"
            >
              View Details
            </Button>
          </div>
        )}
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-foreground mb-1 line-clamp-1 group-hover:text-primary transition-colors duration-300">
              {project.title}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {project.description}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <span className={`px-2 py-1 rounded-md text-xs font-medium ${getComplexityColor(project.complexity)}`}>
            {project.complexity}
          </span>
          <span className={`px-2 py-1 rounded-md text-xs font-medium ${getStatusColor(project.status)}`}>
            {project.status}
          </span>
          {project.academicProject && (
            <span className="px-2 py-1 rounded-md text-xs font-medium text-secondary bg-secondary/10">
              Academic
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {project.technologies.slice(0, 4).map((tech, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs font-mono"
            >
              {tech.name}
            </span>
          ))}
          {project.technologies.length > 4 && (
            <span className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs font-mono">
              +{project.technologies.length - 4}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Icon name="Code2" size={14} />
              <span>{project.metrics.linesOfCode.toLocaleString()} lines</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="GitCommit" size={14} />
              <span>{project.metrics.commits} commits</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Calendar" size={14} />
            <span>{project.metrics.duration}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;